import {
    Component,
    input,
    output,
    signal,
    computed,
    effect,
    TemplateRef,
    model,
    OnInit,
    OnDestroy,
    untracked,
    ChangeDetectorRef,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { DialogComponent } from '../../../@core/components/ui/dialog/dialog.component';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox/checkbox.component';
import { SelectComponent } from '../../../@core/components/ui/select/select.component';
import { SelectOption } from '../../../@core/components/ui/select/select.model';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import {
    FileSelectionConfig,
    DropdownOption,
    FileItem,
    SelectedFileGroup,
    FileSelectionChangeEvent,
    FileSelectionSubmitEvent,
    FetchFunction,
    FileSelectionType,
    FileSelectionMode,
    TabType,
} from './file-selection.model';
import { FileIconComponent } from "../file-icon/file-icon.component";
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { FileDropComponent } from '../file-drop/file-drop.component';
import { FileDropTypes } from '../file-drop/file-drop.types';
import { ToastService } from 'src/app/@core/services/toast.service';
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';
import { IconComponent, IconSource, IconSourceEnum } from "src/app/@core/components/ui/icon/icon.component";

@Component({
    selector: 'app-file-selection',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogComponent,
        PaginatorModule,
        TabsModule,
        TooltipModule,
        SkeletonModule,
        ButtonComponent,
        CheckboxComponent,
        SelectComponent,
        SearchBarComponent,
        FileIconComponent,
        FileDropComponent,
        AssetUrlPipe,
        IconComponent
    ],
    templateUrl: './file-selection.component.html',
    styleUrls: ['./file-selection.component.scss'],
})
export class FileSelectionComponent implements OnInit, OnDestroy {
    // Model binding for dialog visibility (two-way binding)
    visible = model<boolean>(false);

    // Mode: 'dialog' (default) or 'inline' (renders as a plain div, no dialog wrapper)
    readonly FileSelectionMode = FileSelectionMode;
    mode = input<FileSelectionMode>(FileSelectionMode.DIALOG);

    @ViewChild(FileDropComponent) fileDropRef?: FileDropComponent;
    @ViewChild('paginatorContainer') paginatorContainerRef?: ElementRef<HTMLElement>;

    // Input signals
    config = input.required<FileSelectionConfig>();
    fetch = input.required<FetchFunction>();
    dropdownOptions = input<DropdownOption[] | undefined>(undefined);
    allDropdownOptions = signal<DropdownOption[] | undefined>(undefined);

    // Custom template inputs
    fileItemTemplate = input<TemplateRef<any> | null>(null);
    selectedFileTemplate = input<TemplateRef<any> | null>(null);
    emptyStateTemplate = input<TemplateRef<any> | null>(null);
    selectedEmptyTemplate = input<TemplateRef<any> | null>(null);
    header = input<TemplateRef<any> | null>(null);
    bodyHeader = input<TemplateRef<any> | null>(null);
    afterDropdownTemplate = input<TemplateRef<any> | null>(null);
    afterDropdownTemplateTabId = input<string | undefined>(undefined);

    // Output signals
    onSubmit = output<FileSelectionSubmitEvent>();
    onCancel = output<void>();

    // Internal state signals
    selectedTabIndex = signal<number>(0);
    selectedDropdown = signal<string | null>(null);
    currentPage = signal<number>(0);
    pageSize = signal<number>(10);
    searchQuery = signal<string>('');
    selectAllChecked = signal<boolean>(false);
    totalUploadedFileSize = signal<number>(0);
    isSubmitting = signal<boolean>(false);
    currentFolderId = signal<string | null>(null);
    folderBreadcrumb = signal<{ id: string; name: string }[]>([]);

    private hasSetInitialTab = signal<boolean>(false);

    pageLinkSize = signal<number>(3);
    private paginatorResizeObserver?: ResizeObserver;

    supportSubFolders = computed(() => !!this.config().supportSubFolders);

    hasSelectableItems = computed(() => this.items().filter((item: FileItem) => !item.isFolder).length > 0);

    // Computed signal for max individual file size for local uploads (defaults to 50MB)
    localUploadMaxFileSize = computed<number>(() => {
        return this.config().localUploadMaxFileSize || 50 * 1000 * 1000; // Default 50MB
    });


    // Map to store selected files: key = `${tabId}_${dropdownId}_${fileId}`
    private selectedFilesMap = signal<Map<string, { file: FileItem; tabId: string; dropdownId: string }>>(
        new Map()
    );

    // Internal signals for data management (public for template access)
    items = signal<FileItem[]>([]);
    loading = signal<boolean>(false);
    totalRecords = signal<number>(0);

    // Subjects for reactive programming
    private filterSubject = new Subject<FileSelectionChangeEvent>();
    private destroy$ = new Subject<void>();

    // Computed signals
    selectedTab = computed(() => {
        const tabs = this.config().tabs;
        if (!tabs || tabs.length === 0) {
            return null;
        }
        const index = this.selectedTabIndex();
        return tabs[index] || tabs[0];
    });

    dropdownSelectOptions = computed((): SelectOption[] => {
        return (this.dropdownOptions() || []).map((option) => ({
            label: option.label,
            value: option.id,
            ...(option.icon && {
                icon: option.icon,
                iconSource: option.iconSource as IconSource | undefined,
                iconSize: option.iconSize as SelectOption['iconSize'],
                iconClass: option.iconClass,
            }),
        }));
    });

    showAfterDropdownTemplate = computed(
        () =>
            !!(
                this.afterDropdownTemplate() &&
                this.afterDropdownTemplateTabId() &&
                this.selectedTab()?.id === this.afterDropdownTemplateTabId()
            )
    );

    rowsPerPageOptions = computed(() => {
        return this.config().rowsPerPageOptions || [10, 20, 50];
    });

    // Computed signal to determine if items should be displayed
    shouldShowItems = computed(() => {
        const tabs = this.config().tabs;
        const hasTabs = tabs && tabs.length > 0;
        const hasDropdownOptions = (this.dropdownOptions() || []).length > 0;
        const selectedDropdown = this.selectedDropdown();
        const hasSelectedDropdown = selectedDropdown !== null && selectedDropdown !== undefined && selectedDropdown !== '';

        // Show items if:
        // - No tabs exist, OR
        // - Tabs exist but no dropdown options (always show), OR
        // - Tabs exist with dropdown options AND a dropdown is selected
        return !hasTabs || !hasDropdownOptions || hasSelectedDropdown;
    });

    // Allowed file types - per-tab override via metadata, then config, then default
    allowedFileTypes = computed(() => {
        // If the tab has allowed file types, return them
        const tab = this.selectedTab();
        if (tab?.metadata?.['allowedFileTypes']) {
            return tab.metadata['allowedFileTypes'];
        }
        return this.config().allowedFileTypes || [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC];
    });

    uploadMaxFiles = computed(() => this.selectedTab()?.metadata?.['maxFiles'] as number | undefined);

    // Computed signal for uploaded files (to sync with file-drop component)
    uploadedFiles = computed((): File[] => {
        const files: File[] = [];
        this.selectedFilesMap().forEach(({ file }) => {
            if (file.metadata?.isUploaded && file.metadata?.uploadedFile) {
                files.push(file.metadata.uploadedFile as File);
            }
        });
        return files;
    });

    // Computed signal for grouped selected files
    selectedFileGroups = computed((): SelectedFileGroup[] => {
        const groups: Map<string, SelectedFileGroup> = new Map();
        const filesMap = this.selectedFilesMap();
        const tabs = this.config().tabs || [];
        const configGroupByKey = this.config().groupByKey;

        filesMap.forEach(({ file, tabId, dropdownId }) => {
            // Find the dropdown option to check if it has a groupKey
            const dropdown = (this.allDropdownOptions() || []).find((d) => d.id === dropdownId);

            // Use dropdown's groupKey if available, otherwise fall back to config's groupByKey
            const groupByKey = dropdown?.groupKey || configGroupByKey;

            // Check if file has a value in the groupByKey field
            const groupKeyValue = groupByKey ? this.getNestedValue(file, groupByKey) : null;

            // Get groupLabel value only if dropdown has a groupLabel path defined
            // If using config's groupByKey (no dropdown groupLabel), groupLabelValue will be null
            // and we'll fall back to using groupKeyValue as the label
            const groupLabelValue = groupByKey && groupKeyValue && dropdown?.groupLabel
                ? this.getNestedValue(file, dropdown.groupLabel)
                : null;

            // Use groupKeyValue if it exists and is not empty, otherwise use default tabId_dropdownId grouping
            const key = groupKeyValue && groupKeyValue.trim() !== '' ? `group_${groupKeyValue}` : `${tabId}_${dropdownId}`;

            if (!groups.has(key)) {
                const tab = tabs.find((t) => t.id === tabId);
                const useGroupKey = groupKeyValue && groupKeyValue.trim() !== '';

                // Determine groupLabel: use groupLabelValue if available, otherwise use dropdown label, otherwise use groupKeyValue
                let finalGroupLabel: string | undefined;
                if (useGroupKey) {
                    if (groupLabelValue && groupLabelValue.trim() !== '') {
                        finalGroupLabel = groupLabelValue;
                    } else if (dropdown?.label) {
                        finalGroupLabel = dropdown.label;
                    } else {
                        finalGroupLabel = groupKeyValue;
                    }
                }

                groups.set(key, {
                    tabId: tabId || '',
                    tabLabel: tab?.label || tabId || 'Files',
                    dropdownId,
                    dropdownLabel: dropdown?.label || dropdownId,
                    files: [],
                    groupKey: useGroupKey ? groupKeyValue : undefined,
                    groupLabel: finalGroupLabel,
                });
            }

            groups.get(key)!.files.push(file);
        });

        return Array.from(groups.values());
    });

    allSelectedFiles = computed((): FileItem[] => {
        return Array.from(this.selectedFilesMap().values()).map(({ file, tabId, dropdownId }) => ({
            ...file,
            metadata: { ...file.metadata, tabId, dropdownId },
        }));
    });

    // Computed signal for hierarchical grouping: first by tab, then by dropdown/project
    selectedFileGroupsByTab = computed(() => {
        const groups = this.selectedFileGroups();
        const tabMap = new Map<string, { tabId: string; tabLabel: string; subGroups: SelectedFileGroup[]; count: number }>();

        groups.forEach(group => {
            if (!tabMap.has(group.tabId)) {
                tabMap.set(group.tabId, {
                    tabId: group.tabId,
                    tabLabel: group.tabLabel,
                    subGroups: [],
                    count: 0
                });
            }
            tabMap.get(group.tabId)!.subGroups.push(group);
            tabMap.get(group.tabId)!.count += group.files.length;
        });

        return Array.from(tabMap.values());
    });

    // Check if select all should be checked/indeterminate (only consider selectable files, not folders)
    selectAllState = computed(() => {
        const currentItems = this.items();
        const selectableItems = currentItems.filter((item) => !item.isFolder);
        if (selectableItems.length === 0) {
            return { checked: false, indeterminate: false };
        }

        const selectedCount = selectableItems.filter((item) => this.isFileSelected(item)).length;

        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        } else if (selectedCount === selectableItems.length) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    });

    constructor(
        private toastService: ToastService,
        private cdr: ChangeDetectorRef
    ) {
        // Fetch data with debouncing and switchMap for request cancellation
        this.filterSubject
            .pipe(
                debounceTime(300),
                switchMap((event) => {
                    this.loading.set(true);
                    return this.fetch()(event).pipe(
                        catchError((error) => {
                            console.error('Error fetching files:', error);
                            this.loading.set(false);
                            this.items.set([]);
                            this.totalRecords.set(0);
                            return of({ items: [], totalRecords: 0 });
                        })
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((result) => {
                this.items.set(result.items);
                this.totalRecords.set(result.totalRecords);
                this.loading.set(false);
            });

        // Effect to sync selected state with items
        effect(() => {
            const currentItems = this.items();
            const dropdown = this.selectedDropdown();

            // Update selected state for displayed items
            currentItems.forEach((item) => {
                item.selected = this.isFileSelected(item);
            });

            // Update select all state
            const state = this.selectAllState();
            this.selectAllChecked.set(state.checked);
        });

        // Effect to emit filter change when tab/dropdown/page changes
        effect(
            () => {
                const tab = this.selectedTab();
                const dropdown = this.selectedDropdown();
                const page = this.currentPage();
                const size = this.pageSize();
                const search = this.searchQuery();

                this.emitFilterChange();
            }
        );

        // Effect to trigger initial fetch when dialog opens
        effect(
            () => {
                if (!this.visible() || this.hasSetInitialTab()) return;

                const tabs = this.config().tabs;
                const activeTabId = this.config().activeTabId;

                if (tabs && tabs.length > 0 && activeTabId) {
                    const activeTabIndex = tabs.findIndex(tab => tab.id === activeTabId);
                    if (activeTabIndex >= 0) {
                        untracked(() => {
                            this.selectedTabIndex.set(activeTabIndex);
                        });
                    }
                }

                this.emitFilterChange();
                untracked(() => {
                    this.hasSetInitialTab.set(true);
                });
            }
        );

        // Effect to load initial selected files when dialog opens
        effect(() => {
            const isVisible = this.visible();
            const initialFiles = this.config().initialSelectedFiles;

            if (!isVisible && this.mode() !== FileSelectionMode.INLINE) return;

            // Always reset when initialSelectedFiles is explicitly provided (even if empty),
            // so stale selections from a previous context are cleared on every reopen.
            if (initialFiles != null) {
                untracked(() => {
                    this.selectedFilesMap.set(new Map());
                    this.totalUploadedFileSize.set(0);

                    initialFiles.forEach(file => {
                        const tabId = file.metadata?.tabId || 'upload';
                        const dropdownId = file.metadata?.dropdownId || '';

                        this.selectedFilesMap.update((currentMap) => {
                            const newMap = new Map(currentMap);
                            newMap.set(file.id, { file: { ...file }, tabId, dropdownId });
                            return newMap;
                        });
                        const fileSize = this.getFileSize(file);
                        this.totalUploadedFileSize.update(prev => prev + (fileSize || 0));
                    });
                });
            }
        });

        // Effect to set selected dropdown to first option if dropdown options exist
        effect(() => {
            const dropdownOptions = this.dropdownOptions();
            if (!dropdownOptions || dropdownOptions.length === 0) {
                untracked(() => {
                    this.selectedDropdown.set(null);
                });
                return
            };
            const isSelectedOptionExists = untracked(() => dropdownOptions?.some((option) => option.id === this.selectedDropdown()));
            if (dropdownOptions && !isSelectedOptionExists) {
                untracked(() => {
                    this.selectedDropdown.set(dropdownOptions[0]?.id || null);
                });
            }
        });

        effect(() => {
            const options = this.dropdownOptions();
            if (!options?.length) return;

            this.allDropdownOptions.update(prev => [...(prev || []), ...options]);
        });
    }

    ngOnInit(): void {
        // Initialize with first tab if tabs exist
        const tabs = this.config().tabs;
        const activeTabId = this.config().activeTabId;

        if (tabs && tabs.length > 0) {
            if (activeTabId) {
                const activeTabIndex = tabs.findIndex(tab => tab.id === activeTabId);
                this.selectedTabIndex.set(activeTabIndex >= 0 ? activeTabIndex : 0);
            } else {
                this.selectedTabIndex.set(0);
            }
        }

        // Initialize page size from config
        this.pageSize.set(this.config().defaultPageSize || 10);

        // Observe paginator container width to compute pageLinkSize dynamically
        setTimeout(() => {
            const el = this.paginatorContainerRef?.nativeElement;
            if (!el) return;
            this.paginatorResizeObserver = new ResizeObserver(entries => {
                const width = entries[0]?.contentRect.width ?? el.clientWidth;
                this.pageLinkSize.set(this.computePageLinkSize(width));
            });
            this.paginatorResizeObserver.observe(el);
            this.pageLinkSize.set(this.computePageLinkSize(el.clientWidth));
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.paginatorResizeObserver?.disconnect();
    }

    /**
     * Compute pageLinkSize so the paginator fits in one line.
     * Layout: prev-btn + next-btn + first-btn + last-btn = 4 nav buttons (~28px each)
     * + p-select (~80px) + gaps (~16px) + each page link (~28px)
     */
    private computePageLinkSize(containerWidth: number): number {
        const navButtonsWidth = 4 * 28; // 4 nav buttons
        const selectWidth = 80;
        const gaps = 16;
        const pageLinkWidth = 28;
        const available = containerWidth - navButtonsWidth - selectWidth - gaps;
        const size = Math.max(1, Math.floor(available / pageLinkWidth));
        return Math.min(size, 5);
    }

    /**
     * Handle tab change
     */
    handleTabChange(event: string | number | undefined): void {
        let index: number;

        // valueChange event passes the value directly (string | number | undefined)
        if (event === undefined || event === null) {
            index = 0;
        } else if (typeof event === 'string') {
            index = parseInt(event, 10);
        } else if (typeof event === 'number') {
            index = event;
        } else {
            // Fallback
            index = 0;
        }

        // Only update if the index actually changed
        if (this.selectedTabIndex() !== index) {
            this.selectedTabIndex.set(index);
            this.selectedDropdown.set(null);
            this.currentPage.set(0);
            this.searchQuery.set('');
            this.currentFolderId.set(null);
            this.folderBreadcrumb.set([]);
        }
    }

    /**
     * Handle dropdown change
     */
    handleDropdownChange(event: any): void {
        this.selectedDropdown.set(event.value);
        this.currentPage.set(0);
        this.currentFolderId.set(null);
        this.folderBreadcrumb.set([]);
    }

    /**
     * Handle page change
     */
    handlePageChange(event: PaginatorState): void {
        this.currentPage.set(event.page || 0);

        // Update page size if it changed
        if (event.rows && event.rows !== this.pageSize()) {
            this.pageSize.set(event.rows);
            // Reset to first page when page size changes
            this.currentPage.set(0);
        }
    }

    /**
     * Handle search input
     */
    handleSearchInput(query: string): void {
        this.searchQuery.set(query);
        this.currentPage.set(0);
    }

    /**
     * Toggle select all
     */
    toggleSelectAll(event: any): void {
        if (this.isSubmitting()) return;
        const checked = event.checked ?? event;
        const currentItems = this.items();
        if (currentItems.length === 0) return;

        const tab = this.selectedTab();
        const dropdown = this.selectedDropdown();
        const tabs = this.config().tabs;
        const hasTabs = tabs && tabs.length > 0;
        const hasDropdownOptions = (this.dropdownOptions() || []).length > 0;

        // If tabs exist and dropdown options are provided, require dropdown selection.
        // If tabs exist but no dropdown options, allow selection with default dropdownId.
        if (hasTabs && hasDropdownOptions && !dropdown) return;

        const tabId = tab?.id || '';
        const dropdownId = dropdown || '';
        const selectableItems = currentItems.filter((item) => !item.isFolder);

        if (checked) {
            const itemsToSelect = selectableItems.filter(item => !this.isFileSelected(item));
            const totalSizeToAdd = itemsToSelect.reduce((sum, item) => {
                return sum + (this.getFileSize(item) || 0);
            }, 0);

            const currentTotalSize = this.totalUploadedFileSize();
            const newTotalSize = currentTotalSize + totalSizeToAdd;
            const maxSize = this.config().maxTotalUploadedFileSize;

            // Check if adding this file would exceed total file count limit
            const currentTotalFiles = this.selectedFilesMap().size;
            const newTotalFiles = currentTotalFiles + itemsToSelect.length;
            const maxTotalFiles = this.config().maxTotalUploadedAttachments;
            if (maxTotalFiles && newTotalFiles > maxTotalFiles) {
                this.selectAllChecked.set(false);
                const checkboxComponent = event.checkboxComponent;
                checkboxComponent?.uncheck();
                this.cdr.detectChanges();
                this.toastService.error(
                    `Maximum ${maxTotalFiles} attachments allowed`,
                    `You can only attach up to ${maxTotalFiles} files. Please remove some files before adding more.`
                );
                return;
            }

            // Only validate if maxTotalUploadedFileSize is provided
            if (maxSize && newTotalSize > maxSize) {
                const maxSizeMB = Math.floor(maxSize / (1000 * 1000));
                const checkboxComponent = event.checkboxComponent;
                checkboxComponent?.uncheck();
                this.toastService.error(
                    `Total selected files size cannot exceed ${maxSizeMB}MB. Please remove some files before selecting all.`
                );
                return;
            }

            // Update total size before adding files
            this.totalUploadedFileSize.update(prev => prev + totalSizeToAdd);
        }

        selectableItems.forEach((item) => {
            if (checked) {
                this.addFileToSelection(item, tabId, dropdownId);
            } else {
                this.removeFileFromSelection(item);
            }
        });
    }

    /**
     * Toggle individual file selection
     */
    toggleFileSelection(file: FileItem, event: any): void {
        if (this.isSubmitting()) return;
        const checked = event.checked ?? event;
        const fileSize = this.getFileSize(file);
        const tab = this.selectedTab();
        const dropdown = this.selectedDropdown();
        const tabs = this.config().tabs;
        const hasTabs = tabs && tabs.length > 0;
        const hasDropdownOptions = (this.dropdownOptions() || []).length > 0;

        // If tabs exist and dropdown options are provided, require dropdown selection.
        // If tabs exist but no dropdown options, allow selection with default dropdownId.
        if (hasTabs && hasDropdownOptions && !dropdown) return;

        const tabId = tab?.id || '';
        // When no dropdown options exist, use empty string for grouping (dropdown will be null)
        // When dropdown options exist, use the selected dropdown id
        const dropdownId = dropdown || '';
        if (checked) {
            // Check if adding this file would exceed total file count limit
            const currentTotalFiles = this.selectedFilesMap().size;
            const newTotalFiles = currentTotalFiles + 1;
            const maxTotalFiles = this.config().maxTotalUploadedAttachments;
            if (maxTotalFiles && newTotalFiles > maxTotalFiles) {
                const checkboxComponent = event.checkboxComponent;
                checkboxComponent?.uncheck();
                this.toastService.error(
                    `Maximum ${maxTotalFiles} attachments allowed`,
                    `You can only attach up to ${maxTotalFiles} files. Please remove some files before adding more.`
                );
                return;
            }
            // Check if adding this file would exceed the total size limit (for all files)
            const currentTotalSize = this.totalUploadedFileSize();
            const newTotalSize = currentTotalSize + (fileSize || 0);
            const maxTotalSize = this.config().maxTotalUploadedFileSize;

            // Only validate if maxTotalUploadedFileSize is provided
            if (maxTotalSize && newTotalSize > maxTotalSize) {
                const checkboxComponent = event.checkboxComponent;
                checkboxComponent?.uncheck();
                const maxSizeMB = Math.floor(maxTotalSize / (1000 * 1000));
                this.toastService.error(
                    `Total selected files size cannot exceed ${maxSizeMB}MB. Please remove some files before adding more.`
                );
                return;
            }

            this.totalUploadedFileSize.update(prev => prev + (fileSize || 0));
            this.addFileToSelection(file, tabId, dropdownId);
        } else {
            this.removeFileFromSelection(file);
        }
    }

    /**
     * Check if a file is selected
     */
    isFileSelected(file: FileItem): boolean {
        return this.selectedFilesMap().has(file.id);
    }

    /**
     * Add file to selection
     */
    private addFileToSelection(file: FileItem, tabId: string, dropdownId: string): void {
        // Embed tabId/dropdownId in metadata so they are preserved in the emitted FileItem
        // and can be used to restore selections via initialSelectedFiles on reopen.
        const enrichedFile = { ...file, metadata: { ...file.metadata, tabId, dropdownId } };
        this.selectedFilesMap.update((currentMap) => {
            const newMap = new Map(currentMap);
            newMap.set(file.id, { file: enrichedFile, tabId, dropdownId });
            return newMap;
        });
    }

    /**
     * Remove file from selection
     */
    private removeFileFromSelection(file: FileItem): void {
        const fileSize = this.getFileSize(file);

        // Revoke blob URL if it exists to prevent memory leaks
        if (file.metadata?.blobUrl) {
            URL.revokeObjectURL(file.metadata.blobUrl);
        }

        // Use update to ensure signal reactivity
        this.selectedFilesMap.update((currentMap) => {
            const newMap = new Map(currentMap);
            newMap.delete(file.id);
            return newMap;
        });
        // Subtract file size from total
        this.totalUploadedFileSize.update(prev => Math.max(0, prev - (fileSize || 0)));
    }

    /**
     * Remove selected file (from right panel)
     */
    removeSelectedFile(file: FileItem): void {
        if (this.isSubmitting()) return;
        this.removeFileFromSelection(file);
    }

    /**
     * Handle file upload from file-drop component
     */
    onFileRemove(file: File): void {
        if (this.isSubmitting()) return;
        // Find the FileItem that corresponds to this File object by comparing name and size
        const fileItem = Array.from(this.selectedFilesMap().values()).find(
            ({ file: existingFile }) =>
                existingFile.metadata?.isUploaded &&
                existingFile.name === file.name &&
                existingFile.size === file.size
        );

        if (fileItem) {
            this.removeFileFromSelection(fileItem.file);
        }
    }

    onFileUpload(files: File | File[]): void {
        if (this.isSubmitting()) return;
        const filesArray = Array.isArray(files) ? files : (files ? [files] : []);
        const currentTab = this.selectedTab();
        const tabId = currentTab?.id || 'upload';

        const filesToUpload: File[] = [];
        filesArray.forEach(file => {
            // Check for duplicates (same name and size)
            const isDuplicate = Array.from(this.selectedFilesMap().values()).some(
                ({ file: existingFile }) =>
                    existingFile.metadata?.isUploaded &&
                    existingFile.name === file.name &&
                    existingFile.size === file.size
            );

            if (!isDuplicate) {
                filesToUpload.push(file);
            }
        });

        // Check if adding these files would exceed the maximum number of attachments 
        const maxAttachments = this.config()?.maxTotalUploadedAttachments;
        if (maxAttachments != null) {
            const currentAttachmentsCount = this.allSelectedFiles().length;
            const totalNewAttachments = currentAttachmentsCount + filesToUpload.length;
            if (totalNewAttachments > maxAttachments) {
                this.toastService.error(
                    `Maximum ${maxAttachments} attachments allowed`,
                    `You can only attach up to ${maxAttachments} files. Please remove some files before adding more.`
                );
                this.fileDropRef?.resetToExistingFiles();
                return;
            }
        }

        // Check if adding these files would exceed the size limit
        const totalSizeToAdd = filesToUpload.reduce((sum, file) => sum + (file.size || 0), 0);
        const currentTotalSize = this.totalUploadedFileSize();
        const newTotalSize = currentTotalSize + totalSizeToAdd;
        const maxTotalSize = this.config().maxTotalUploadedFileSize;

        // Only validate if maxTotalUploadedFileSize is provided
        if (maxTotalSize && newTotalSize > maxTotalSize) {
            const maxSizeMB = Math.floor(maxTotalSize / (1000 * 1000));
            this.toastService.error(
                `Total selected files size cannot exceed ${maxSizeMB}MB. Please remove some files before uploading more.`
            );
            this.fileDropRef?.resetToExistingFiles();
            return;
        }

        // Update total size before adding files
        this.totalUploadedFileSize.update(prev => prev + totalSizeToAdd);

        filesToUpload.forEach(file => {
            // Create a blob URL once and cache it in metadata
            const blobUrl = URL.createObjectURL(file);

            // Create a FileItem from the File object
            const fileItem: FileItem = {
                id: `uploaded-${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                metadata: {
                    uploadedFile: file, // Store the actual File object
                    isUploaded: true,
                    blobUrl: blobUrl // Cache the blob URL to prevent creating new ones
                }
            };

            this.addFileToSelection(fileItem, tabId, 'Uploaded');
        });
    }



    /**
     * Emit filter change event (debounced)
     */
    private emitFilterChange(): void {
        const tab = this.selectedTab();
        const dropdown = this.selectedDropdown();
        const page = this.currentPage();
        const size = this.pageSize();
        const search = this.searchQuery();

        this.loading.set(true);

        this.filterSubject.next({
            tabId: tab?.id || '',
            dropdownId: dropdown,
            page,
            pageSize: size,
            searchQuery: search,
            parentFolderId: this.currentFolderId(),
        });
    }

    navigateToFolder(folderId: string, folderName: string): void {
        const breadcrumb = [...this.folderBreadcrumb(), { id: folderId, name: folderName }];
        this.folderBreadcrumb.set(breadcrumb);
        this.currentFolderId.set(folderId);
        this.currentPage.set(0);
        this.emitFilterChange();
    }

    navigateBack(): void {
        const breadcrumb = this.folderBreadcrumb();
        if (breadcrumb.length === 0) {
            this.currentFolderId.set(null);
        } else {
            const newBreadcrumb = breadcrumb.slice(0, -1);
            this.folderBreadcrumb.set(newBreadcrumb);
            this.currentFolderId.set(newBreadcrumb.length > 0 ? newBreadcrumb[newBreadcrumb.length - 1].id : null);
        }
        this.currentPage.set(0);
        this.emitFilterChange();
    }

    /**
     * Handle dialog close
     */
    handleDialogClose(): void {
        this.handleCancel();
    }

    /**
     * Handle cancel
     */
    handleCancel(): void {
        this.resetDialogState();
        this.visible.set(false);
        this.onCancel.emit();
    }

    /**
     * Handle submit
     */
    handleSubmit(): void {
        this.isSubmitting.set(true);
        const selectedGroups = this.selectedFileGroups();
        const allFiles = this.allSelectedFiles();

        // Separate uploaded files from selected files
        const uploadedFiles: File[] = [];
        const selectedFiles: SelectedFileGroup[] = [];

        selectedGroups.forEach(group => {
            const groupUploadedFiles: FileItem[] = [];
            const groupSelectedFiles: FileItem[] = [];

            group.files.forEach(file => {
                if (file.metadata?.isUploaded) {
                    groupUploadedFiles.push(file);
                } else {
                    groupSelectedFiles.push(file);
                }
            });

            // Collect uploaded File objects
            if (groupUploadedFiles.length > 0) {
                uploadedFiles.push(...groupUploadedFiles.map(f => f.metadata.uploadedFile as File));
            }

            // Create groups for selected files
            if (groupSelectedFiles.length > 0) {
                selectedFiles.push({
                    ...group,
                    files: groupSelectedFiles
                });
            }
        });

        // Count total new attachments (both selected and uploaded files)
        const totalSelectedFilesCount = selectedFiles.reduce((sum, group) => sum + group.files.length, 0);
        const totalNewAttachments = uploadedFiles.length + totalSelectedFilesCount;

        // Validation for maxTotalUploadedAttachments only when provided
        const maxAttachments = this.config()?.maxTotalUploadedAttachments;
        if (maxAttachments != null && totalNewAttachments > maxAttachments) {
            this.toastService.error(
                `Maximum ${maxAttachments} attachments allowed`,
                `You can only attach up to ${maxAttachments} files. Please remove some files before adding more.`
            );
            this.isSubmitting.set(false);
            return;
        }

        // Validation for uploaded files (individual file size check - 50MB limit)
        if (uploadedFiles.length > 0) {
            const maxFileSize = this.localUploadMaxFileSize();
            const oversizedFiles = uploadedFiles.filter(file => file.size > maxFileSize);
            if (oversizedFiles.length > 0) {
                const fileNames = oversizedFiles.map(f => f.name).join(', ');
                this.toastService.error(
                    'Locally uploaded file size exceeds limit',
                    `The following file(s) exceed the ${Math.floor(maxFileSize / (1000 * 1000))}MB limit: ${fileNames}`
                );
                this.isSubmitting.set(false);
                return;
            }
        }

        // Emit based on what we have (include activeTabId so parent knows which tab was active at submit)
        const activeTabId = this.selectedTab()?.id;
        if (uploadedFiles.length > 0 && selectedFiles.length === 0) {
            // Only locally uploaded files
            this.onSubmit.emit({
                type: FileSelectionType.UPLOADED_FILES,
                activeTabId,
                uploadedFiles: uploadedFiles,
            });
        } else if (selectedFiles.length > 0 && uploadedFiles.length === 0) {
            // Only selected files
            const nonUploadedFiles = allFiles.filter(f => !f.metadata?.isUploaded);
            this.onSubmit.emit({
                type: FileSelectionType.SELECTED_FILES,
                activeTabId,
                selectedFiles: selectedFiles,
                allSelectedFiles: nonUploadedFiles,
            });
        } else if (uploadedFiles.length > 0 && selectedFiles.length > 0) {
            // Both types - emit with both
            const nonUploadedFiles = allFiles.filter(f => !f.metadata?.isUploaded);
            this.onSubmit.emit({
                type: FileSelectionType.SELECTED_FILES,
                activeTabId,
                selectedFiles: selectedFiles,
                allSelectedFiles: nonUploadedFiles,
                uploadedFiles: uploadedFiles
            });
        }
        // Note: resetDialogState() is now called only on successful API response in parent component
    }

    /**
     * Reset dialog state to initial values
     * This should only be called on successful API response, not on errors
     */
    resetDialogState(): void {
        // Revoke all blob URLs before clearing selections to prevent memory leaks
        this.selectedFilesMap().forEach(({ file }) => {
            if (file.metadata?.blobUrl) {
                URL.revokeObjectURL(file.metadata.blobUrl);
            }
        });

        // Clear all selections
        this.selectedFilesMap.set(new Map());

        // Reset search and pagination
        this.searchQuery.set('');
        this.currentPage.set(0);

        // Reset dropdown to null (will be set to first option when reopened)
        this.selectedDropdown.set(null);

        // Clear items
        this.items.set([]);
        this.totalRecords.set(0);

        // Reset select all checkbox
        this.selectAllChecked.set(false);

        // Reset total uploaded file size
        this.totalUploadedFileSize.set(0);

        // Reset submitting state
        this.isSubmitting.set(false);

        // Clear accumulated dropdown options
        this.allDropdownOptions.set(undefined);

        // Reset initial tab flag
        this.hasSetInitialTab.set(false);

        this.currentFolderId.set(null);
        this.folderBreadcrumb.set([]);
    }

    disableSubmitButton() {
        const hasSelectedFiles = this.allSelectedFiles().length > 0;
        const maxTotalSize = this.config().maxTotalUploadedFileSize;

        // Only check size limit if maxTotalUploadedFileSize is provided
        const exceedsSizeLimit = maxTotalSize ? this.totalUploadedFileSize() > maxTotalSize : false;

        // Disable button if no files selected OR if total size exceeds limit
        return !hasSelectedFiles || exceedsSizeLimit;
    }

    /**
     * Get TabType enum for template usage
     */
    get TabType() {
        return TabType;
    }

    /**
     * Get display title
     */
    getTitle(): string {
        return this.config().title || 'Upload file';
    }

    /**
     * Get submit button label
     */
    getSubmitLabel(): string {
        return this.config().submitButtonLabel || 'Upload';
    }

    /**
     * Get cancel button label
     */
    getCancelLabel(): string {
        return this.config().cancelButtonLabel || 'Cancel';
    }

    /**
     * Get search placeholder
     */
    getSearchPlaceholder(): string {
        return this.config().searchPlaceholder || 'Search with name';
    }

    /**
     * Get select all label
     */
    getSelectAllLabel(): string {
        const config = this.config();
        const count = this.items().length;
        return config.selectAllLabel || `Select all (${count})`;
    }

    getFolderIconType(): string {
        return this.selectedTab()?.folderIcon ?? 'book-open-text';
    }

    getFolderIconSource(): IconSource {
        return this.selectedTab()?.folderIconSource as IconSource ?? IconSourceEnum.LUCIDE;
    }

    /**
     * Format file size
     */
    formatFileSize(bytes?: number): string {
        if (!bytes) return '';

        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1000 * 1000) return (bytes / 1000).toFixed(2) + ' KB';
        return (bytes / (1000 * 1000)).toFixed(2) + ' MB';
    }

    /**
     * Get nested value from object using dot notation path (e.g., "metadata.file")
     */
    private getNestedValue(obj: any, path: string): string | null {
        if (!path || !obj) return null;

        const keys = path.split('.');
        let value = obj;

        for (const key of keys) {
            if (value == null || typeof value !== 'object') {
                return null;
            }
            value = value[key];
        }

        // Return the value as string if it exists, otherwise null
        return value != null ? String(value) : null;
    }

    /**
     * Get file size from FileItem, checking multiple possible locations
     */
    private getFileSize(file: FileItem): number {
        // Try direct size property first
        if (file.size) {
            return file.size;
        }

        // Try metadata.originalDoc.fileSize (for documents)
        if (file.metadata?.originalDoc?.fileSize) {
            return file.metadata.originalDoc.fileSize;
        }

        // Try metadata.originalFile.size (for repository files)
        if (file.metadata?.originalFile?.size) {
            return file.metadata.originalFile.size;
        }

        // Return 0 if size cannot be determined
        return 0;
    }

    getFileType(fileName: string): FILE_TYPE | undefined {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf':
                return FILE_TYPE.PDF;
            case 'doc':
            case 'docx':
                return FILE_TYPE.WORD;
            case 'xls':
            case 'xlsx':
                return FILE_TYPE.EXCEL;
            case 'ppt':
            case 'pptx':
                return FILE_TYPE.POWERPOINT;
            case 'html':
                return FILE_TYPE.HTML;
            case 'txt':
                return FILE_TYPE.TEXT;
            case 'csv':
                return FILE_TYPE.CSV;
            case 'md':
                return FILE_TYPE.MARKDOWN;
            case 'python':
                return FILE_TYPE.PYTHON;
            case 'json':
                return FILE_TYPE.JSON;
            case 'jpg':
                return FILE_TYPE.JPG;
            case 'png':
                return FILE_TYPE.PNG;
            case 'jpeg':
                return FILE_TYPE.JPEG;
            default:
                return undefined;
        }
    }


    getImageUrl(file: FileItem): string {
        if (file.metadata?.isUploaded && file.metadata?.uploadedFile) {
            // Return cached blob URL if it exists, otherwise create and cache it
            if (file.metadata.blobUrl) {
                return file.metadata.blobUrl;
            }
            // Fallback: create blob URL if not cached (shouldn't happen in normal flow)
            const blobUrl = URL.createObjectURL(file.metadata.uploadedFile as File);
            file.metadata.blobUrl = blobUrl;
            return blobUrl;
        }

        // Handle pre-selected attachments that are images
        if (file.metadata?.isAttachment && file.metadata?.originalAttachment?.attachmentData?.fullUrl) {
            const type = this.getFileType(file.name);
            if (type === FILE_TYPE.JPG || type === FILE_TYPE.PNG || type === FILE_TYPE.JPEG) {
                return file.metadata.originalAttachment.attachmentData.fullUrl;
            }
        }

        return '';
    }

    /**
     * Get toggle selection function for a specific file (for use in custom templates)
     */
    getToggleSelectionFn(file: FileItem): (event: any) => void {
        return (event: any) => this.toggleFileSelection(file, event);
    }

    /**
     * Get toggle selection function for a selected file template (right panel).
     * The selected panel only needs to support unselecting/removing.
     */
    getSelectedToggleSelectionFn(file: FileItem): (event: any) => void {
        return (event: any) => {
            const checked = event?.checked ?? event;
            if (!checked) {
                this.removeSelectedFile(file);
            }
        };
    }
}

